import React, { useState, useCallback, useRef, useEffect } from "react";
import Request from "../../../utils/Request";
import { api_version } from "../../../settings";
import { Button, Form, Table, Spinner, Accordion, Card } from "react-bootstrap";
import { connect } from "react-redux";
import { responseValidate } from "../../../utils/functions";
import Store from "../../../store/index";
import { setNewToast } from "../../../store/reducers/App";

const questionList = [
  `What is the item cost of item name in the last few invoices?`,
  `Which items were included in the last few invoices from vendor name vendor?`,
  `Show me all invoices from vendor name vendor?`,
  `When was the last time I bought item name?`,
];

const inputBoxStyle = {
  border: "none",
  boxShadow: "none",
  outline: "none",
  flex: 1,
  fontSize: "1rem",
};

const sendBtnStyle = {
  borderRadius: "50%",
  width: "40px",
  height: "40px",
  padding: "0",
  marginLeft: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const AiDocInfo = ({ store }) => {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const chatEndRef = useRef(null);
  const firstAccordionRef = useRef(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      const lastMsg = chatHistory[chatHistory.length - 1];

      if (
        lastMsg?.role === "assistant" &&
        lastMsg?.data &&
        firstAccordionRef.current
      ) {
        firstAccordionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [chatHistory]);

  const sendMessage = useCallback(
    async (text) => {
      if (!text.trim()) return;

      setChatHistory((prev) => [...prev, { role: "user", content: text }]);
      setLoading(true);
      setMessage("");

      try {
        const response = await Request.post(`${api_version}/ask-ai`, {
          question: text,
          store_id: store?.id,
        });

        const newMessage = response?.data?.status
          ? {
              role: "assistant",
              content: "Here's what I found for your request.",
              data: response.data.data,
            }
          : {
              role: "assistant",
              content: "No results found for your question.",
            };

        setChatHistory((prev) => [...prev, newMessage]);
      } catch {
        setChatHistory((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              "Something went wrong while processing your request. Please try again.",
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [store]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage(message);
      }
    },
    [message, sendMessage]
  );

  const handleDocClick = useCallback(async (doc_url) => {
    const response = await Request.post(`${api_version}/get-doc-view`, {
      doc_url,
    });
    const result = responseValidate(response);
    if (result.status) {
      window.open(response.data.data, "_blank");
    } else {
      Store.dispatch(setNewToast({ body: result.message, variant: "danger" }));
    }
  }, []);

  const formatHeader = useCallback(
    (key) => key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    []
  );

  const renderGroupedTables = useCallback(
    (invoiceDetails) => {
      const vendorMap = new Map();

      invoiceDetails.forEach((docGroup) => {
        const rows = docGroup.items || [];

        rows.forEach((row) => {
          const rawKeys = Object.keys(row).filter(
            (key) => !["doc_url", "doc_name", "vendor_name"].includes(key)
          );

          const sortedKeys = rawKeys
            .filter((key) => key !== "invoice_date")
            .sort();
          const keys = ["invoice_date", ...sortedKeys]
            .filter((key) => rawKeys.includes(key))

          const keySignature = keys.join("|");

          if (!vendorMap.has(row.vendor_name)) {
            vendorMap.set(row.vendor_name, new Map());
          }

          const keyMap = vendorMap.get(row.vendor_name);

          if (!keyMap.has(keySignature)) {
            keyMap.set(keySignature, { keys, rows: [] });
          }

          keyMap.get(keySignature).rows.push(row);
        });
      });

      const groupedData = Array.from(vendorMap.entries()).map(
        ([vendor_name, keyMap]) => ({
          vendor_name,
          tables: Array.from(keyMap.values()),
        })
      );

      return (
        <Accordion defaultActiveKey="0" className="mt-3">
          {groupedData.map((vendorGroup, index) => (
            <Card
              key={index}
              className="mb-3"
              ref={index === 0 ? firstAccordionRef : null}
            >
              <Accordion.Toggle
                as={Card.Header}
                eventKey={String(index)}
                style={{
                  cursor: "pointer",
                  fontWeight: "bold",
                  color: "#000",
                }}
              >
                🏷️ Vendor: {vendorGroup.vendor_name}
              </Accordion.Toggle>
              <Accordion.Collapse eventKey={String(index)}>
                <Card.Body className="p-3">
                  {vendorGroup.tables.map((table, tableIndex) => (
                    <div key={tableIndex} style={{ overflowX: "auto" }}>
                      <Table
                        bordered
                        size="sm"
                        className="mb-4"
                        key={tableIndex}
                        style={{ fontSize: "0.85rem" }}
                      >
                        <thead
                          style={{
                            color: "#000",
                            backgroundColor: "#f8f9fa",
                            fontWeight: "bold",
                          }}
                        >
                          <tr>
                            {[...table.keys, "Invoice_View"].map((key) => (
                              <th key={key}>{formatHeader(key)}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {table.rows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              {[...table.keys, "Invoice_View"].map((key) => (
                                <td key={key}>
                                  {key === "Invoice_View" ? (
                                    row.doc_url ? (
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() =>
                                          handleDocClick(row.doc_url)
                                        }
                                      >
                                        View →
                                      </Button>
                                    ) : (
                                      <span className="text-muted">N/A</span>
                                    )
                                  ) : (
                                    row[key] || (
                                      <span className="text-muted">N/A</span>
                                    )
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ))}
                </Card.Body>
              </Accordion.Collapse>
            </Card>
          ))}
        </Accordion>
      );
    },
    [formatHeader, handleDocClick]
  );

  return (
    <div className="container-fluid">
      <Card className="shadow-sm p-4 mt-3">
        <h4 className="mb-4 d-flex align-items-center">
          <i className="fas fa-robot me-2"></i> AI Chat Assistant
        </h4>
        {chatHistory?.length === 0 && (
          <div
            className="d-flex flex-wrap gap-2 justify-content-center mb-4 p-2"
            style={{ backgroundColor: "#f9f9f9", borderRadius: "10px" }}
          >
            {questionList.map((q, idx) => (
              <Button
                key={idx}
                onClick={() => setMessage(q)}
                variant="primary"
                className="px-3 py-2 fw-bold border rounded-pill text-center mt-2 ai-question-btn"
              >
                {q}
              </Button>
            ))}
          </div>
        )}
        <div
          className="chat-box mb-4"
          style={{
            maxHeight: "400px",
            overflowY: "auto",
            paddingRight: "10px",
          }}
        >
          {chatHistory.map((msg, idx) => (
            <div
              key={idx}
              className={`d-flex mb-3 ${
                msg.role === "user"
                  ? "justify-content-end"
                  : "justify-content-start"
              }`}
            >
              <div
                className={`p-3 rounded shadow-sm ${
                  msg.role === "user"
                    ? "bg-primary text-white"
                    : "bg-light text-dark"
                }`}
                style={{ maxWidth: "100%", whiteSpace: "pre-wrap" }}
              >
                {msg.content}
                {msg.data && (
                  <div className="mt-3">{renderGroupedTables(msg.data)}</div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="d-flex justify-content-start mb-3">
              <div className="p-3 bg-light rounded shadow-sm text-dark">
                <em>AI is Finding...</em>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        <div
          className="d-flex align-items-center mt-3"
          style={{
            border: "1px solid #ccc",
            borderRadius: "20px",
            padding: "10px 15px",
            backgroundColor: "#fff",
          }}
        >
          <Form.Control
            type="text"
            placeholder="Type your message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            style={inputBoxStyle}
          />
          <Button
            variant="primary"
            onClick={() => sendMessage(message)}
            disabled={loading || !message.trim()}
            style={sendBtnStyle}
          >
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <i className="fas fa-paper-plane" />
            )}
          </Button>
        </div>
        <small className="mt-2 text-muted d-flex justify-content-center" style={{ fontSize: "0.75rem" }}>
           AI can make mistakes.
        </small>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  store: state.App.store,
});

export default connect(mapStateToProps)(React.memo(AiDocInfo));
